import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PegasusLogo from "@/components/PegasusMark";
import { ArrowLeft } from "lucide-react";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      const msg =
        err?.response?.data?.detail || err?.message || "Login failed.";
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col bg-[#0A0A0A]"
      data-testid="admin-login-page"
    >
      <header className="border-b border-white/5 px-6 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <PegasusLogo className="h-10" />
        </Link>
        <Link
          to="/"
          className="text-sm text-[#A3A3A3] hover:text-[#D4AF37] flex items-center gap-2"
        >
          <ArrowLeft size={14} /> Back to site
        </Link>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <form
          onSubmit={submit}
          className="w-full max-w-md bg-[#141414] border border-white/10 p-10"
          data-testid="admin-login-form"
        >
          <div className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37] mb-3">
            Admin Access
          </div>
          <h1 className="font-serif text-3xl text-white mb-8">Sign in</h1>

          <label className="block mb-5">
            <span className="block text-[11px] uppercase tracking-[0.25em] text-[#A3A3A3] mb-2">
              Email
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] text-white px-4 py-3 outline-none focus:border-[#D4AF37]"
              data-testid="admin-login-email"
            />
          </label>
          <label className="block mb-8">
            <span className="block text-[11px] uppercase tracking-[0.25em] text-[#A3A3A3] mb-2">
              Password
            </span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] text-white px-4 py-3 outline-none focus:border-[#D4AF37]"
              data-testid="admin-login-password"
            />
          </label>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#D4AF37] hover:bg-[#E5C665] text-black py-3.5 text-sm tracking-wide transition-colors disabled:opacity-60"
            data-testid="admin-login-submit"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
